import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * 受保护的路由组件
 * 用于保护需要登录才能访问的普通用户页面
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoggedIn, loading } = useAuth();
  const navigate = useNavigate();
  const [waitingForAuth, setWaitingForAuth] = useState(false);

  useEffect(() => {
    // 等待加载完成后检查登录状态
    if (!loading && !isLoggedIn) {
      // 检查是否有token（可能是SSO登录但获取用户信息失败）
      const token = localStorage.getItem('userToken');
      const urlParams = new URLSearchParams(window.location.search);
      const hasAccessToken = urlParams.has('access_token');
      
      if (token || hasAccessToken) {
        // 有token或URL中有access_token，可能是SSO登录过程中
        // 等待一下，给SSO登录流程更多时间
        console.log('⚠️ [ProtectedRoute] 检测到token或access_token，等待认证完成...');
        setWaitingForAuth(true);
        
        const timer = setTimeout(() => {
          // 5秒后如果还是没有用户信息，重定向到登录页
          if (!isLoggedIn) {
            console.warn('⚠️ [ProtectedRoute] 等待超时，重定向到登录页');
            setWaitingForAuth(false);
            const currentPath = window.location.pathname + window.location.search;
            sessionStorage.setItem('redirectPath', currentPath);
            navigate('/auth?redirect=true');
          } else {
            setWaitingForAuth(false);
          }
        }, 5000);
        
        return () => {
          clearTimeout(timer);
          setWaitingForAuth(false);
        };
      } else {
        // 没有token，直接重定向到登录页
        const currentPath = window.location.pathname + window.location.search;
        sessionStorage.setItem('redirectPath', currentPath);
        navigate('/auth?redirect=true');
      }
    } else if (isLoggedIn) {
      // 如果已经登录，清除等待状态
      setWaitingForAuth(false);
    }
  }, [loading, isLoggedIn, navigate]);

  // 显示加载状态或等待认证状态
  if (loading || waitingForAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {waitingForAuth ? '正在验证登录状态...' : '加载中...'}
          </p>
          {waitingForAuth && (
            <p className="text-sm text-gray-500 mt-2">请稍候，这可能需要几秒钟</p>
          )}
        </div>
      </div>
    );
  }

  // 如果没有登录，返回空（将被重定向到登录页面）
  if (!isLoggedIn || !user) {
    return null;
  }

  // 已登录，渲染子组件
  return <>{children}</>;
}

